import {FC} from 'react'

import {CookiesType, getCookiesContent} from '@/content/cookies'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../common/content/Text'
import {CheckCircleIcon} from '../common/icons/CheckCircleIcon'
import {Switcher} from '../inputs/Switcher'

type CookiesSettingsProps = {
  title?: string
  otherAllowed: boolean
  setOtherAllowed: (otherAllowed: boolean) => void
}

export const CookiesSettings: FC<React.PropsWithChildren<CookiesSettingsProps>> = ({
  title,
  otherAllowed,
  setOtherAllowed,
}) => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {types, allowedTitle, forbiddenTitle} = getCookiesContent(language)

  const handleCookiesChange = (value: boolean) => {
    setOtherAllowed(value)
  }

  return (
    <>
      {title && (
        <div className="title">
          <Text
            variant={TextVariant.h4}
            element="h2"
            align="center"
            color={theme.palette.text.primary}
          >
            {title}
          </Text>
        </div>
      )}
      <ul>
        {Object.entries(types).map(([key, {title, description}]) => (
          <li key={key} className="item">
            <div className="header">
              <Text variant={TextVariant.h5} element="p" fontWeight={TextFontWeight.bold}>
                {t(title)}
              </Text>
              {key === CookiesType.necessary ? (
                <div className="wrapper">
                  <CheckCircleIcon
                    backgroundColor={theme.palette.success.main}
                    checkColor={theme.palette.background.default}
                  />
                  <div className="value">
                    <Text
                      variant={TextVariant.h5}
                      element="p"
                      color={theme.palette.success.main}
                      fontWeight={TextFontWeight.bold}
                    >
                      {t(allowedTitle)}
                    </Text>
                  </div>
                </div>
              ) : key === CookiesType.other ? (
                <div className="wrapper">
                  <Switcher isAllowed={otherAllowed} onChange={handleCookiesChange} />
                  <div className="value">
                    <Text
                      variant={TextVariant.h5}
                      element="p"
                      color={otherAllowed ? theme.palette.success.main : theme.palette.text.light}
                      fontWeight={TextFontWeight.bold}
                    >
                      {otherAllowed ? t(allowedTitle) : t(forbiddenTitle)}
                    </Text>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
            <div className="description">
              <Text>{t(description)}</Text>
            </div>
          </li>
        ))}
      </ul>

      <style jsx>{`
        .title {
          margin-bottom: ${theme.spacing[4]};
        }
        .item + .item {
          margin-top: ${theme.spacing[5]};
        }
        .header {
          display: flex;
          justify-content: space-between;
        }
        .wrapper {
          display: flex;
          align-items: center;
        }
        .value {
          min-width: 95px;
          margin-left: ${theme.spacing[3]};
        }
        .description {
          margin-top: ${theme.spacing[3]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .title {
            margin-bottom: ${theme.spacing[3]};
          }
          .item + .item {
            margin-top: ${theme.spacing[3]};
          }
          .description {
            margin-top: ${theme.spacing[2]};
          }
          .wrapper {
            margin-left: ${theme.spacing[3]};
          }
          .value {
            min-width: 85px;
            margin-left: ${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
